import type { AttributionData } from "@fluid-app/api-client";
import type { StorageManager } from "@fluid-app/storage-manager";
import { STORAGE_KEYS } from "@fluid-app/storage-manager";

import { debug } from "../utils/debug";

// ServerAttributionData is now the same as AttributionData from API client

/**
 * Interface for attribution override configuration
 * This allows setting attribution that overrides server-calculated attribution
 */
export interface AttributionOverride {
  attribution?: AttributionData;
}

/**
 * Attribution manager that handles attribution from settings API
 * and supports attribution overrides with priority handling
 */
export class AttributionManager {
  private storageManager: StorageManager;
  private urlAttributionCache = new Map<string, AttributionData | null>();

  constructor(storageManager: StorageManager) {
    this.storageManager = storageManager;
  }

  /**
   * Store attribution data from settings API response
   * @param url The URL this attribution data is for
   * @param attribution Attribution data from settings API (API client format)
   */
  public storeServerAttribution(
    url: string,
    attribution: AttributionData | null,
  ): void {
    debug(
      "AttributionManager: Storing server attribution for URL:",
      url,
      attribution,
    );

    // Cache attribution data for this URL
    this.urlAttributionCache.set(url, attribution);

    // Store current attribution data
    if (attribution) {
      this.storageManager.setObject(STORAGE_KEYS.ATTRIBUTION, attribution);
      debug("AttributionManager: Stored server attribution:", attribution);
    } else {
      this.storageManager.removeItem(STORAGE_KEYS.ATTRIBUTION);
      debug("AttributionManager: No server attribution for URL:", url);
    }
  }

  /**
   * Get effective attribution data respecting priority:
   * 1. Attribution override (highest priority)
   * 2. Server-calculated attribution
   * 3. Fallback (null)
   */
  public getEffectiveAttribution(url?: string): AttributionData | null {
    debug("AttributionManager: Getting effective attribution for URL:", url);

    // 1. Check for attribution override (highest priority)
    const attributionOverride = this.getAttributionOverride();
    if (attributionOverride) {
      debug(
        "AttributionManager: Using attribution override:",
        attributionOverride,
      );
      return attributionOverride;
    }

    // 2. Check for server-calculated attribution
    const serverAttribution = this.getServerAttribution(url);
    if (serverAttribution) {
      debug(
        "AttributionManager: Using server-calculated attribution:",
        serverAttribution,
      );
      return serverAttribution;
    }

    // 3. No attribution available
    debug("AttributionManager: No attribution available");
    return null;
  }

  /**
   * Get server-calculated attribution for the current or specified URL
   */
  public getServerAttribution(url?: string): AttributionData | null {
    const currentUrl =
      url ?? (typeof window !== "undefined" ? window.location.href : "");

    // First check memory cache
    const cached = this.urlAttributionCache.get(currentUrl);
    if (cached !== undefined) {
      return cached;
    }

    // Check current attribution data in storage
    const stored = this.storageManager.getObject<AttributionData>(
      STORAGE_KEYS.ATTRIBUTION,
    );
    if (stored) {
      debug("Using stored server attribution:", stored);
      return stored;
    }

    return null;
  }

  /**
   * Set attribution override that overrides server-calculated attribution
   */
  public setAttributionOverride(
    attributionOverride: AttributionOverride,
  ): void {
    debug("Setting attribution override:", attributionOverride);
    const storedOverrides =
      this.storageManager.getObject<{
        attributionOverride?: AttributionOverride;
      }>(STORAGE_KEYS.SETTINGS_OVERRIDE) ?? {};
    storedOverrides.attributionOverride = attributionOverride;
    this.storageManager.setObject(
      STORAGE_KEYS.SETTINGS_OVERRIDE,
      storedOverrides,
    );
  }

  /**
   * Clear attribution override, falling back to server-calculated attribution
   */
  public clearAttributionOverride(): void {
    debug("Clearing attribution override");
    const storedOverrides =
      this.storageManager.getObject<{
        attributionOverride?: AttributionOverride;
      }>(STORAGE_KEYS.SETTINGS_OVERRIDE) ?? {};
    delete storedOverrides.attributionOverride;
    this.storageManager.setObject(
      STORAGE_KEYS.SETTINGS_OVERRIDE,
      storedOverrides,
    );
  }

  /**
   * Get attribution override if set
   */
  public getAttributionOverride(): AttributionData | null {
    const storedOverrides = this.storageManager.getObject<{
      attributionOverride?: AttributionOverride;
    }>(STORAGE_KEYS.SETTINGS_OVERRIDE);

    if (storedOverrides?.attributionOverride?.attribution) {
      return storedOverrides.attributionOverride.attribution;
    }

    return null;
  }

  /**
   * Check if attribution data needs refresh for the current URL
   */
  public needsRefresh(url: string): boolean {
    // Always needs refresh if no data for this URL
    if (!this.urlAttributionCache.has(url)) {
      return true;
    }

    // Check if cached data is expired
    const cacheKey = this.getCacheKey(url);
    const cache = this.getAttributionCache();
    const cachedEntry = cache[cacheKey];

    return !cachedEntry || !this.isCacheValid(cachedEntry);
  }

  /**
   * Clear attribution data for a specific URL or all URLs
   */
  public clearAttribution(url?: string): void {
    if (url) {
      this.urlAttributionCache.delete(url);
      const cacheKey = this.getCacheKey(url);
      const cache = this.getAttributionCache();
      delete cache[cacheKey];
      this.storageManager.setObject(STORAGE_KEYS.ATTRIBUTION_CACHE, cache);
    } else {
      // Clear all attribution data
      this.urlAttributionCache.clear();
      this.storageManager.removeItem(STORAGE_KEYS.ATTRIBUTION);
      this.storageManager.removeItem(STORAGE_KEYS.ATTRIBUTION_CACHE);
    }
  }

  /**
   * Get attribution source information for debugging
   */
  public getAttributionSource(): "attribution-override" | "server" | "none" {
    if (this.getAttributionOverride()) {
      return "attribution-override";
    }
    if (this.getServerAttribution()) {
      return "server";
    }
    return "none";
  }

  /**
   * Generate cache key for URL-based attribution storage
   */
  private getCacheKey(url: string): string {
    try {
      const parsedUrl = new URL(url);
      // Use pathname + search + hash for cache key to distinguish different pages
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      // Fallback to full URL if parsing fails
      return url;
    }
  }

  /**
   * Get attribution cache from storage
   */
  private getAttributionCache(): Record<
    string,
    {
      attribution: AttributionData;
      timestamp: number;
      url: string;
    }
  > {
    return this.storageManager.getObject(STORAGE_KEYS.ATTRIBUTION_CACHE) ?? {};
  }

  /**
   * Check if cached attribution data is still valid (1 hour TTL)
   */
  private isCacheValid(cachedEntry: {
    attribution: AttributionData;
    timestamp: number;
    url: string;
  }): boolean {
    const TTL = 60 * 60 * 1000; // 1 hour
    return Date.now() - cachedEntry.timestamp < TTL;
  }
}
